import { NavLink } from 'react-router-dom';
import { useTk } from '@/lib/i18n';

interface TabDef {
  to: string;
  labelKey: string;
  icon: 'home' | 'groupon' | 'discover' | 'messages' | 'me';
  end?: boolean;
}

/**
 * Koom GO Mini App — 5-tab 底部导航。
 *
 * 视觉规格（DESIGN.md §Components > Bottom Tab Bar）：
 *   - 5 grid columns；中间 column 是凸起的圆形 "探店" 按钮
 *   - 标准 cell：22×22 icon + 11px label；idle `neutral.600` / active `brand.primary.700`
 *   - Center "探店" 按钮：56×56 圆，`brand.primary.500` 填充，KoomGoMark 在 `neutral.900` 色
 *     绝对定位，悬浮 12px 上方，自带 0px 顶部缺口让 koom-mark 视觉穿出
 *   - bar 底色 `neutral.0` 85% + `backdrop-blur-md`；顶部 1px `neutral.200` 描边
 *   - 必须清掉 iOS home indicator / Android gesture bar safe-area（`env(safe-area-inset-bottom)`）
 *
 * Tab 顺序：首页 / 团购 / 探店（中心凸起） / 消息 / 我的
 */
export function BottomNav(): React.ReactElement {
  const tk = useTk();
  const tabs: TabDef[] = [
    { to: '/',         labelKey: 'nav.home',     icon: 'home',     end: true },
    { to: '/groupons', labelKey: 'nav.groupon',  icon: 'groupon' },
    { to: '/discover', labelKey: 'nav.discover', icon: 'discover' },
    { to: '/messages', labelKey: 'nav.messages', icon: 'messages' },
    { to: '/me',       labelKey: 'nav.me',       icon: 'me' },
  ];

  return (
    <nav
      aria-label="bottom navigation"
      className="
        fixed bottom-0 left-0 right-0 z-30
        grid grid-cols-5
        border-t border-neutral-200
        bg-neutral-0/90 backdrop-blur-md
        pb-[env(safe-area-inset-bottom)]
      "
    >
      {tabs.map((tab) =>
        tab.icon === 'discover' ? (
          <CenterDiscoverTab key={tab.to} tab={tab} label={tk(tab.labelKey)} />
        ) : (
          <StandardTab key={tab.to} tab={tab} label={tk(tab.labelKey)} />
        ),
      )}
    </nav>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Standard tab (4 corners)
// ────────────────────────────────────────────────────────────────────────────

function StandardTab({ tab, label }: { tab: TabDef; label: string }): React.ReactElement {
  return (
    <NavLink
      to={tab.to}
      end={tab.end}
      className={({ isActive }) =>
        [
          'relative flex flex-col items-center justify-center gap-0.5',
          'pt-2.5 pb-2 text-[11px] font-medium transition-colors',
          isActive ? 'text-brand-primary-700' : 'text-neutral-600 hover:text-brand-primary-600',
        ].join(' ')
      }
    >
      {({ isActive }) => (
        <>
          <NavGlyph name={tab.icon} active={isActive} />
          <span>{label}</span>
        </>
      )}
    </NavLink>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Center raised "Discover" tab
// ────────────────────────────────────────────────────────────────────────────

function CenterDiscoverTab({ tab, label }: { tab: TabDef; label: string }): React.ReactElement {
  return (
    <div className="relative flex items-end justify-center pb-2">
      <NavLink
        to={tab.to}
        className="flex flex-col items-center gap-0.5"
      >
        {({ isActive }) => (
          <>
            {/* Raised circle — absolutely positioned so it floats above the bar.
                The center column reserves visual space for the label only;
                the circle peeks 16px above the bar's top edge. */}
            <span
              aria-hidden
              className={[
                'absolute -top-4 left-1/2 -translate-x-1/2',
                'grid h-14 w-14 place-items-center',
                'rounded-full',
                'shadow-lg ring-4 ring-neutral-0',
                'transition-transform duration-fast',
                isActive
                  ? 'bg-brand-primary-500 scale-105'
                  : 'bg-brand-primary-500',
              ].join(' ')}
            >
              <KoomGoMarkSvg />
            </span>
            <span
              className={[
                'mt-12 text-[11px] font-semibold',
                isActive ? 'text-brand-primary-700' : 'text-neutral-600',
              ].join(' ')}
            >
              {label}
            </span>
          </>
        )}
      </NavLink>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Glyphs
// ────────────────────────────────────────────────────────────────────────────

function NavGlyph({
  name,
  active,
}: {
  name: TabDef['icon'];
  active: boolean;
}): React.ReactElement {
  const stroke = active ? '#A16207' /* brand.primary.700 */ : '#585049' /* neutral.600 */;
  const fill = active ? '#FEF3C7' /* brand.primary.100 */ : 'none';
  switch (name) {
    case 'home':
      return (
        <svg width="22" height="22" viewBox="0 0 24 24" fill={fill} stroke={stroke} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <path d="M3 11l9-7 9 7v9a1 1 0 0 1-1 1h-5v-6h-6v6H4a1 1 0 0 1-1-1z" />
        </svg>
      );
    case 'groupon':
      return (
        <svg width="22" height="22" viewBox="0 0 24 24" fill={fill} stroke={stroke} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <path d="M5 7h14l-1.4 10.2a2 2 0 0 1-2 1.8H8.4a2 2 0 0 1-2-1.8z" />
          <path d="M9 7V5a3 3 0 0 1 6 0v2" />
        </svg>
      );
    case 'discover':
      // not used here (center tab renders its own koomgo mark) — keep for type completeness
      return (
        <svg width="22" height="22" viewBox="0 0 24 24" fill={fill} stroke={stroke} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <circle cx="12" cy="12" r="9" />
          <path d="M14.5 9.5l-1.7 4.8-4.8 1.7 1.7-4.8z" />
        </svg>
      );
    case 'messages':
      return (
        <svg width="22" height="22" viewBox="0 0 24 24" fill={fill} stroke={stroke} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <path d="M21 12a8 8 0 0 1-11.6 7.1L4 21l1.9-5.4A8 8 0 1 1 21 12z" />
        </svg>
      );
    case 'me':
      return (
        <svg width="22" height="22" viewBox="0 0 24 24" fill={fill} stroke={stroke} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <circle cx="12" cy="8" r="3.5" />
          <path d="M5 20c1.4-3.6 4-5.4 7-5.4s5.6 1.8 7 5.4" />
        </svg>
      );
  }
}

/** Inline mark used inside the center raised circle. Pupils are locked to neutral.900. */
function KoomGoMarkSvg(): React.ReactElement {
  return (
    <svg
      width="34"
      height="20"
      viewBox="0 0 56 32"
      fill="none"
      aria-hidden
      className="text-neutral-900"
    >
      <circle cx="18" cy="16" r="14" fill="currentColor" />
      <circle cx="38" cy="16" r="14" fill="currentColor" />
      {/* Inverse pupils: yellow center against the dark koom mark ring */}
      <circle cx="18" cy="16" r="4.5" fill="#FFC400" />
      <circle cx="38" cy="16" r="4.5" fill="#FFC400" />
    </svg>
  );
}
